/**
 * ConfidenceIndicator Component
 * Visual representation of AI confidence scores
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

/**
 * Get color classes based on confidence score
 */
const getConfidenceColors = (score) => {
  if (score >= 90) return { bg: 'bg-green-500', text: 'text-green-500', bgLight: 'bg-green-100 dark:bg-green-900/30' };
  if (score >= 75) return { bg: 'bg-emerald-500', text: 'text-emerald-500', bgLight: 'bg-emerald-100 dark:bg-emerald-900/30' };
  if (score >= 60) return { bg: 'bg-yellow-500', text: 'text-yellow-500', bgLight: 'bg-yellow-100 dark:bg-yellow-900/30' };
  if (score >= 40) return { bg: 'bg-orange-500', text: 'text-orange-500', bgLight: 'bg-orange-100 dark:bg-orange-900/30' };
  return { bg: 'bg-red-500', text: 'text-red-500', bgLight: 'bg-red-100 dark:bg-red-900/30' };
};

/**
 * Get confidence label
 */
const getConfidenceLabel = (score) => {
  if (score >= 90) return 'Excellent';
  if (score >= 75) return 'Good';
  if (score >= 60) return 'Moderate';
  if (score >= 40) return 'Low';
  return 'Very Low';
};

/**
 * Get confidence icon
 */
const getConfidenceIcon = (score) => {
  if (score >= 75) return CheckCircle;
  if (score >= 50) return Minus;
  return AlertTriangle;
};

/**
 * Badge variant of confidence indicator
 */
export function ConfidenceBadge({ score, showLabel = false, size = 'default', className }) {
  const colors = getConfidenceColors(score);
  const label = getConfidenceLabel(score);

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    default: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full font-medium',
              colors.bgLight,
              colors.text,
              sizeClasses[size],
              className
            )}
            data-testid="confidence-badge"
          >
            <span>{score}%</span>
            {showLabel && <span className="hidden sm:inline">({label})</span>}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>Confidence: {label} ({score}%)</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Progress bar variant of confidence indicator
 */
export function ConfidenceBar({ score, showLabel = true, showPercentage = true, size = 'default', className }) {
  const colors = getConfidenceColors(score);
  const label = getConfidenceLabel(score);

  const heightClasses = {
    sm: 'h-1.5',
    default: 'h-2',
    lg: 'h-3',
  };

  return (
    <div className={cn('w-full', className)} data-testid="confidence-bar">
      {(showLabel || showPercentage) && (
        <div className="flex items-center justify-between mb-1">
          {showLabel && (
            <span className={cn('text-xs font-medium', colors.text)}>{label}</span>
          )}
          {showPercentage && (
            <span className="text-xs text-muted-foreground">{score}%</span>
          )}
        </div>
      )}
      <div className={cn('w-full rounded-full bg-muted', heightClasses[size])}>
        <div
          className={cn('rounded-full transition-all duration-500', colors.bg, heightClasses[size])}
          style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Circular/ring variant of confidence indicator
 */
export function ConfidenceRing({ score, size = 'default', showLabel = false, className }) {
  const colors = getConfidenceColors(score);
  const label = getConfidenceLabel(score);
  const Icon = getConfidenceIcon(score);

  const sizeConfig = {
    sm: { container: 'w-12 h-12', text: 'text-xs', stroke: 3 },
    default: { container: 'w-16 h-16', text: 'text-sm', stroke: 4 },
    lg: { container: 'w-24 h-24', text: 'text-lg', stroke: 5 },
  };

  const config = sizeConfig[size];
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className={cn('relative flex items-center justify-center', config.container, className)} data-testid="confidence-ring">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={config.stroke}
          className="text-muted"
        />
        {/* Progress circle */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={config.stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className={colors.text}
          style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn('font-bold', colors.text, config.text)}>{score}%</span>
        {showLabel && (
          <span className="text-[10px] text-muted-foreground">{label}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Compact inline confidence indicator
 */
export function ConfidenceInline({ score, className }) {
  const colors = getConfidenceColors(score);
  const Icon = getConfidenceIcon(score);

  return (
    <span className={cn('inline-flex items-center gap-1', className)} data-testid="confidence-inline">
      <Icon className={cn('h-3.5 w-3.5', colors.text)} />
      <span className={cn('text-sm font-medium', colors.text)}>{score}%</span>
    </span>
  );
}

/**
 * Main ConfidenceIndicator component with variant prop
 */
export function ConfidenceIndicator({ 
  score = 0, 
  variant = 'badge', 
  size = 'default',
  showLabel = false,
  showPercentage = true,
  className 
}) {
  const safeScore = Math.min(100, Math.max(0, Math.round(score)));

  switch (variant) {
    case 'bar':
      return <ConfidenceBar score={safeScore} showLabel={showLabel} showPercentage={showPercentage} size={size} className={className} />;
    case 'ring':
      return <ConfidenceRing score={safeScore} showLabel={showLabel} size={size} className={className} />;
    case 'inline':
      return <ConfidenceInline score={safeScore} className={className} />;
    case 'badge':
    default:
      return <ConfidenceBadge score={safeScore} showLabel={showLabel} size={size} className={className} />;
  }
}

export default ConfidenceIndicator;
